import React, { ChangeEvent, useEffect, useState } from 'react'
import { SlerpLogo } from '@slerp/assets'
import { Input, Form, Button, Col, Row, Layout, Collapse, message } from 'antd'
import { requiredRule, noWhiteSpaceRule, slugInputRule } from './rules'
import { slugify } from '@slerp/helpers'
import { publishNewMerchant, PublishNewMerchantParams } from '@slerp/accounts'
import { Redirect } from 'react-router-dom'
import { StepIndicator } from 'components/Utils'
import { companySuffixRule } from 'components/Settings/Rules'
import { ApolloError } from '@apollo/client'
import { handleCompanyWebsiteFormat } from './utils'
import {
  trackPixelLead,
  trackUserActionsGA4,
  usePageViewTracker
} from 'packages/@slerp/tracker'

type CreateYourSlerpSpace = Array<
  | 'merchant_name'
  | 'slug'
  | 'setting_email'
  | 'setting_website'
  | 'registered_company_name'
>

type CreateAdminAccount = Array<'email' | 'firstname' | 'lastname' | 'password'>

type PublishNewMerchantErrors =
  | 'unique_merchant_slug_constraint'
  | 'unique_user_email_constraint'
  | 'exceeded_40_character_limit'
  | 'invalid_stripe_account'
  | 'no_registration_details'
  | 'multiple_default_transaction_plans'
  | 'no_default_transaction_plan'

interface FormError {
  errorFields: Record<'name', CreateYourSlerpSpace>[] &
    Record<'errors', CreateAdminAccount>[]
  values: Record<string, CreateYourSlerpSpace & CreateAdminAccount>
  outOfDate: boolean
}

const CREATE_YOUR_SLERPSPACE_FIELDS: CreateYourSlerpSpace = [
  'merchant_name',
  'slug',
  'setting_email',
  'setting_website',
  'registered_company_name'
]

const CREATE_ADMIN_ACCOUNT_FIELDS: CreateAdminAccount = [
  'email',
  'firstname',
  'lastname',
  'password'
]

const PUBLISH_ONBOARDING_ERROR_LIST: Record<PublishNewMerchantErrors, string> =
  {
    unique_merchant_slug_constraint:
      'This Slerpspace has already been registered.',
    unique_user_email_constraint:
      'This user has an account already, please try a different email.',
    exceeded_40_character_limit: 'Slug has exceeded the 40 character limit.',
    invalid_stripe_account:
      'The provided stripe key does not have access to account (or that account does not exist). Application access may have been revoked.',
    no_registration_details: 'No registration details found.',
    multiple_default_transaction_plans: 'Multiple default transaction plans.',
    no_default_transaction_plan: 'No default transaction plan.'
  }

const OnboardingForm = () => {
  usePageViewTracker()
  document.title = 'Slerp Controls - Create Your Slerpspace'
  const { useForm } = Form
  const { Header } = Layout
  const { Panel } = Collapse

  const [form] = useForm()
  const [activeKey, setActiveKey] = useState<string>('')
  const [stepState, setStepState] = useState<
    Record<'step-1' | 'step-2', 'inactive' | 'finished'>
  >({
    'step-1': 'inactive',
    'step-2': 'inactive'
  })
  const [sso, setSso] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    localStorage.removeItem('token')
    localStorage.removeItem('merchant')
    localStorage.removeItem('user')

    const timer = setTimeout(() => {
      setActiveKey('step-1')
    }, 200)

    return () => clearTimeout(timer)
  }, [])

  const handleSlugFromEvent = (e: ChangeEvent<HTMLInputElement>): string => {
    if (!e.currentTarget.value) return ''
    const slugified = slugify(e.currentTarget.value, true)

    if ((e.nativeEvent as MessageEvent).data === (' ' || '-')) {
      return slugified + '-'
    }

    return slugified
  }

  const handleCompanyNameFromEvent = (
    e: ChangeEvent<HTMLInputElement>
  ): string => {
    if (!e.currentTarget.value) return ''
    const slugified = slugify(e.currentTarget.value)
    form.setFieldsValue({ slug: slugified })

    return e.currentTarget.value
  }

  const handleCompanyEmailFromEvent = (
    e: ChangeEvent<HTMLInputElement>
  ): string => {
    if (!e.currentTarget.value) return ''

    return e.currentTarget.value.trim()
  }

  const handleNextStep = () => {
    const settingWebsite = form.getFieldValue('setting_website')

    form.setFieldsValue({
      setting_website: handleCompanyWebsiteFormat(settingWebsite)
    })

    setIsLoading(true)

    form
      .validateFields(CREATE_YOUR_SLERPSPACE_FIELDS)
      .then(async (values) => {
        const timer = setTimeout(() => {
          setStepState((prev) => ({
            ...prev,
            'step-1': 'finished'
          }))
          setActiveKey('step-2')
          setIsLoading(false)
        }, 600)

        return () => clearTimeout(timer)
      })
      .catch((error) => {
        setIsLoading(false)
        console.log('error', error)
      })
  }

  const handleOnSubmitErrors = (
    errorCodes: Array<PublishNewMerchantErrors | string>
  ) => {
    if (errorCodes.includes('unique_merchant_slug_constraint')) {
      setActiveKey('step-1')
      form.setFields([
        {
          name: 'slug',
          errors: [
            PUBLISH_ONBOARDING_ERROR_LIST['unique_merchant_slug_constraint']
          ]
        }
      ])
    } else if (errorCodes.includes('unique_user_email_constraint')) {
      setActiveKey('step-2')
      form.setFields([
        {
          name: 'email',
          errors: [
            PUBLISH_ONBOARDING_ERROR_LIST['unique_user_email_constraint']
          ]
        }
      ])
    } else {
      return
    }
  }

  const onFinish = (values: PublishNewMerchantParams) => {
    setIsLoading(true)

    const { slug, setting_website } = values

    // Ensures that no invalid characters retains on submit, e.g. "trailing '-', etc."
    const slugified = slugify(slug)
    const sanitisedValues = {
      ...values,
      slug: slugified
    }

    form.setFieldsValue({
      setting_website: handleCompanyWebsiteFormat(setting_website)
    })

    form
      .validateFields([
        ...CREATE_YOUR_SLERPSPACE_FIELDS,
        ...CREATE_ADMIN_ACCOUNT_FIELDS
      ])
      .then(async () => {
        publishNewMerchant(sanitisedValues)
          .then(({ result }) => {
            setStepState((prev) => ({
              ...prev,
              'step-2': 'finished'
            }))

            trackUserActionsGA4({
              category: 'Onboarding',
              action: 'Lead'
            })
            trackPixelLead()

            setIsLoading(false)
            if (!result) return
            const { sso } = result
            setSso(sso)
          })
          .catch(({ errors }) => {
            console.log('error', errors)
            setIsLoading(false)

            const errorCodes: Array<PublishNewMerchantErrors | string> =
              errors.reduce((acc: string[], error: ApolloError) => {
                return [...acc, error.message]
              }, [])

            const normalizedErrors: string = errorCodes
              .reduce((acc: string, code: string) => {
                const errorMessage =
                  PUBLISH_ONBOARDING_ERROR_LIST[
                    code as PublishNewMerchantErrors
                  ] ?? code

                return acc + `${errorMessage} `
              }, '')
              .trim()

            handleOnSubmitErrors(errorCodes)
            message.error(
              `Unable to create slerpspace due to: "${normalizedErrors}"`,
              10
            )
          })
      })
      .catch((error) => {
        setIsLoading(false)
        console.error('publishNewMerchant error: ', error)
      })
  }

  const onFinishFailed = (errorInfo: FormError) => {
    const { errorFields } = errorInfo
    if (!errorFields.length) return
    if (CREATE_YOUR_SLERPSPACE_FIELDS.includes(errorFields[0].name[0]))
      return setActiveKey('step-1')
  }

  if (sso) return <Redirect to={`/${sso}`} />
  return (
    <div className='onboarding-page'>
      <Header className='site-layout-background main-header' />
      <div className='onboarding-container' data-testid='onboarding-form'>
        <Row justify='center' className='_mb-40'>
          <SlerpLogo
            className='onboarding-logo '
            data-testid='onboarding-slerp-logo'
          />
        </Row>
        <Form
          form={form}
          className='onboarding-form'
          labelCol={{ span: 24, style: { paddingBottom: '0px' } }}
          wrapperCol={{ span: 24 }}
          requiredMark={false}
          onFinish={onFinish}
          // Requires ValidateErrorEntity<PublishNewMerchantParams>,
          // but can't find any 'ValidateErrorEntity' within ANTD, seems like an issue
          onFinishFailed={onFinishFailed as any}
        >
          <Collapse
            accordion
            onChange={(key: string | string[]) => {
              setActiveKey(key as string)
            }}
            activeKey={activeKey}
            className='onboarding-panels'
            data-testid='onboarding-panels'
          >
            <Panel
              key='step-1'
              id='onboarding-create-your-slerpspace'
              header={
                <Row
                  justify='space-between'
                  className='_mb-0 onboarding-header'
                  data-testid='onboarding-create-your-slerpspace'
                >
                  <Col className='onboarding-title' span={19}>
                    <h2 className='_mb-0'>Create your SlerpSpace</h2>
                    <div className='onboarding-subtitle'>
                      <h5>
                        Tell us about your company. You can edit these options
                        at a later time.
                      </h5>
                    </div>
                  </Col>
                  <Col>
                    <StepIndicator
                      step={1}
                      active={activeKey === 'step-1'}
                      finished={stepState['step-1'] === 'finished'}
                    />
                  </Col>
                </Row>
              }
              showArrow={false}
            >
              <div
                onKeyDown={(key) => {
                  const { key: keyType } = key
                  if (keyType !== 'Enter') return
                  key.preventDefault()
                  handleNextStep()
                }}
              >
                <Form.Item
                  label={`What's your company name?`}
                  rules={[...noWhiteSpaceRule]}
                  name='merchant_name'
                  className='_mt-4'
                  getValueFromEvent={handleCompanyNameFromEvent}
                  validateTrigger='onBlur'
                >
                  <Input
                    placeholder='Company name'
                    maxLength={40}
                    data-testid='merchant-name-input'
                    autoFocus
                  />
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.merchant_name !== currentValues.merchant_name
                  }
                  className='_mb-0'
                >
                  {({ getFieldValue }) =>
                    getFieldValue('merchant_name')?.length > 0 ? (
                      <>
                        <Col span={24} className='_mb-8 _mt-4'>
                          Create a domain for your online Slerp store
                        </Col>
                        <Col>
                          <Form.Item
                            className='_mt-4'
                            getValueFromEvent={handleSlugFromEvent}
                            name='slug'
                            rules={[...noWhiteSpaceRule, ...slugInputRule]}
                            dependencies={['merchant_name']}
                            validateTrigger='onBlur'
                          >
                            <Input
                              prefix='https://'
                              suffix='.slerp.com'
                              placeholder='your-domain'
                              value={slugify(getFieldValue('merchant_name'))}
                              className='onboarding-slug'
                              maxLength={40}
                              data-testid='merchant-slug-input'
                            />
                          </Form.Item>
                        </Col>
                      </>
                    ) : null
                  }
                </Form.Item>

                <Form.Item
                  label={`Company email`}
                  rules={[
                    ...requiredRule,
                    {
                      type: 'email',
                      message: 'Please input a valid email address'
                    }
                  ]}
                  name='setting_email'
                  className='_mt-4'
                  validateTrigger='onBlur'
                  getValueFromEvent={handleCompanyEmailFromEvent}
                >
                  <Input
                    placeholder='e.g. defaultemail@slerp.com'
                    data-testid='merchant-email-input'
                  />
                </Form.Item>

                <Form.Item
                  label={`Company website?`}
                  rules={[
                    ...noWhiteSpaceRule,
                    {
                      type: 'url',
                      message: 'Please enter a valid URL.'
                    }
                  ]}
                  name='setting_website'
                  className='_mt-4'
                  validateTrigger='onSubmit'
                >
                  <Input
                    placeholder='e.g. https://slerp.com'
                    data-testid='setting-website-input'
                  />
                </Form.Item>

                <Form.Item
                  label={`Registered company name`}
                  rules={[...companySuffixRule]}
                  name='registered_company_name'
                  className='_mt-4'
                  validateTrigger='onBlur'
                >
                  <Input
                    placeholder='e.g. Slerp, Ltd.'
                    data-testid='registered-company-name-input'
                  />
                </Form.Item>

                <Row justify='end' className='_mb-0'>
                  <Col>
                    <Button
                      className='_mt-24'
                      onClick={handleNextStep}
                      data-testid='onboarding-create-your-slerpspace-next-button'
                      loading={isLoading}
                      disabled={isLoading}
                    >
                      Next
                    </Button>
                  </Col>
                </Row>
              </div>
            </Panel>
            <Panel
              key='step-2'
              id='onboarding-create-admin-account'
              collapsible={
                stepState['step-1'] === 'finished' ? 'header' : 'disabled'
              }
              header={
                <Row
                  justify='space-between'
                  className='_mb-0 onboarding-header'
                  data-testid='onboarding-create-admin-account'
                >
                  <Col
                    className={`onboarding-title ${
                      stepState['step-1'] === 'finished' ? '' : '-disabled'
                    }`}
                    span={19}
                  >
                    <h2 className='_mb-0'>Create your admin account</h2>
                    <div className='onboarding-subtitle'>
                      <h5>You will be first admin user of your SlerpSpace.</h5>
                    </div>
                  </Col>
                  <Col>
                    <StepIndicator
                      step={2}
                      active={activeKey === 'step-2'}
                      finished={stepState['step-2'] === 'finished'}
                    />
                  </Col>
                </Row>
              }
              showArrow={false}
            >
              <>
                <Row justify='space-between' gutter={[24, 8]}>
                  <Col span={12}>
                    <Form.Item
                      label={`First name`}
                      rules={[...noWhiteSpaceRule]}
                      name='firstname'
                      className='_mb-0'
                    >
                      <Input
                        placeholder='First name'
                        data-testid='first-name-input'
                        autoFocus
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={`Last name`}
                      rules={[...noWhiteSpaceRule]}
                      name='lastname'
                      className='_mb-0'
                    >
                      <Input
                        placeholder='Last name'
                        data-testid='last-name-input'
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item
                  label={`Email`}
                  rules={[
                    ...requiredRule,
                    {
                      type: 'email',
                      message: 'Please input a valid email.'
                    }
                  ]}
                  name='email'
                  className='_mt-4'
                  validateTrigger='onBlur'
                >
                  <Input
                    placeholder='admin-user@slerp.com'
                    data-testid='email-input'
                    autoComplete='off'
                  />
                </Form.Item>
                <Form.Item
                  label={`Password`}
                  rules={[...noWhiteSpaceRule]}
                  name='password'
                  className='_mt-4'
                >
                  <Input.Password
                    placeholder='password'
                    visibilityToggle
                    data-testid='password-input'
                    autoComplete='new-password'
                  />
                </Form.Item>
                <Row justify='end' className='_mb-0'>
                  <Col>
                    <Button
                      htmlType='submit'
                      className='_mt-24'
                      name='submit'
                      loading={isLoading}
                      data-testid='onboarding-submit-button'
                    >
                      {isLoading
                        ? 'Starting your slerp journey...'
                        : 'Start your slerp journey'}
                    </Button>
                  </Col>
                </Row>
              </>
            </Panel>
          </Collapse>
        </Form>
      </div>
    </div>
  )
}

export default OnboardingForm
